import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { RootState, useAppDispatch } from '../../store';
import { useOrgDefaultCurrency } from '../../hooks/useOrgDefaultCurrency';
import {
  createMatter,
  addFilesToMatter,
  analyzeMatter,
  fetchMatterDropdowns,
  deleteMatter,
} from '../../store/actions/Matters/MatterActions';
import { describeUploadResult, SequentialUploadResult } from '../../services/uploads/uploadBatching';
import { partitionOversized, oversizedMessage } from '../../utils/uploadLimits';
import { fetchOrgPeople } from '../../store/actions/OnboardingActions';
import { showAddClientSidePanel } from '../../store/actions/sidePanelActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import apis from '../../utils/apis';
import { fetchDataSources } from '../../store/actions/AIStudioAction';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import MatterCreationProgress from './MatterCreationProgress';
import { isRunFinished, isRunLive } from '../shared/brand/aiProgress';
import {
  createdMatterDestination,
  createdMatterRoute,
  matterDetailRoute,
} from './matterCreationNavigation';
import ClientSelector from '../shared/ClientSelector';
import { Collaborator } from '../shared/CollaboratorSelector';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import BrandLoader from '../shared/brand/BrandLoader';
import Toggle from '../shared/TailwindComponents/Toggle';
import { hasCasesSupport } from './matterConstants';
import IndexSourceSelector from './IndexSourceSelector';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import { fetchLinkableProjects } from '../../store/actions/Projects/ProjectActions';
import {
  PiScalesLight,
  PiGavelLight,
  PiDatabaseLight,
  PiListChecksLight,
  PiChatTextLight,
  PiTextAlignLeftLight,
  PiNoteLight,
  PiFileTextLight,
  PiSparkleLight,
} from 'react-icons/pi';
import FileOrDescriptionSelector from '../shared/FileOrDescriptionSelector';
import MatterIntentSelector, { MatterIntent } from './MatterIntentSelector';
import MatterWizard, { MatterWizardData } from './MatterWizard';
import { isMatterEconomicsEnabled } from '../../utils/config';
import { requiresFixedFee, requiresContingency } from './economics/economicsConstants';
import Button from '../shared/TailwindComponents/Button';
import useAIPreferences from '../../hooks/useAIPreferences';
import { AVAILABLE_JURISDICTIONS } from './matterConstants';
import { FeatureTour, WizardStepTip, matterIntentTourSteps, matterIntentTip, matterQuickCreateTip } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import { toErrorText } from '../../utils/errorMessage';

interface AnalysisOptionsValue {
  fetchLaws: boolean;
  fetchPrecedents: boolean;
  fetchDataSource: boolean;
  createTasks: boolean;
  createQuestionnaire: boolean;
  generateDescription: boolean;
  generateNotes: boolean;
  generateDraftSuggestions: boolean;
  generateInsightSuggestions: boolean;
}

const DEFAULT_ANALYSIS_OPTIONS: AnalysisOptionsValue = {
  fetchLaws: true,
  fetchPrecedents: true,
  fetchDataSource: true,
  createTasks: true,
  createQuestionnaire: true,
  generateDescription: true,
  generateNotes: true,
  generateDraftSuggestions: true,
  generateInsightSuggestions: true,
};

const AnalysisOptionsSelector: React.FC<{
  value: AnalysisOptionsValue;
  onChange: (value: AnalysisOptionsValue) => void;
  disabled?: boolean;
  hasDataSources?: boolean;
  jurisdiction?: string;
}> = ({ value, onChange, disabled, hasDataSources, jurisdiction }) => {
  const { t } = useTranslation();
  const opts = value || DEFAULT_ANALYSIS_OPTIONS;
  const showCases = hasCasesSupport(jurisdiction);

  const toggleOption = (key: keyof AnalysisOptionsValue) => {
    onChange({ ...opts, [key]: !opts[key] });
  };

  const researchOptions: { key: keyof AnalysisOptionsValue; label: string; description: string; icon: React.ReactNode }[] = [
    { key: 'fetchLaws', label: t('Applicable Laws'), description: t('Search for relevant statutes and legislation'), icon: <PiScalesLight className="w-5 h-5" /> },
    ...(showCases ? [{ key: 'fetchPrecedents' as const, label: t('Precedents / Case Law'), description: t('Search for relevant case law and precedents'), icon: <PiGavelLight className="w-5 h-5" /> }] : []),
    ...(hasDataSources ? [{ key: 'fetchDataSource' as const, label: t('Connected Data Sources'), description: t('Search your proprietary document collections'), icon: <PiDatabaseLight className="w-5 h-5" /> }] : []),
  ];

  const generationOptions: { key: keyof AnalysisOptionsValue; label: string; description: string; icon: React.ReactNode }[] = [
    { key: 'createTasks', label: t('Suggested Tasks'), description: t('AI-generated action items and next steps'), icon: <PiListChecksLight className="w-5 h-5" /> },
    { key: 'createQuestionnaire', label: t('Client Questionnaire'), description: t('AI-generated questions for client intake'), icon: <PiChatTextLight className="w-5 h-5" /> },
  ];

  const aiGenerationOptions: { key: keyof AnalysisOptionsValue; label: string; description: string; icon: React.ReactNode }[] = [
    { key: 'generateDescription', label: t('Matter Description'), description: t('AI-generated summary and case description'), icon: <PiTextAlignLeftLight className="w-5 h-5" /> },
    { key: 'generateNotes', label: t('Case Notes'), description: t('AI-generated notes and key observations'), icon: <PiNoteLight className="w-5 h-5" /> },
    { key: 'generateDraftSuggestions', label: t('Draft Suggestions'), description: t('Suggested documents to draft for this matter'), icon: <PiFileTextLight className="w-5 h-5" /> },
    { key: 'generateInsightSuggestions', label: t('Insight Suggestions'), description: t('Suggested analyses and insights to generate'), icon: <PiSparkleLight className="w-5 h-5" /> },
  ];

  const renderOptionCard = (opt: { key: keyof AnalysisOptionsValue; label: string; description: string; icon: React.ReactNode }) => (
    <div
      key={opt.key}
      className={`flex items-center justify-between gap-3 rounded-md border p-3 transition-all duration-200 ${
        opts[opt.key]
          ? 'border-champagne/40 bg-champagne/10'
          : 'border-hairline-soft bg-surface-sunken/70'
      } ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`flex-shrink-0 transition-colors duration-200 ${
          opts[opt.key] ? 'text-champagne' : 'text-fg-muted'
        }`}>
          {opt.icon}
        </div>
        <div className="min-w-0">
          <p className="text-sm text-fg-strong truncate">{opt.label}</p>
          <p className="text-xs text-fg-muted truncate">{opt.description}</p>
        </div>
      </div>
      <div className="flex-shrink-0">
        <Toggle enabled={opts[opt.key]} onToggle={() => toggleOption(opt.key)} />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-fg-body mb-2">{t('Legal Research')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {researchOptions.map(renderOptionCard)}
        </div>
      </div>
      <div>
        <p className="text-sm text-fg-body mb-2">{t('AI Generation')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {generationOptions.map(renderOptionCard)}
        </div>
      </div>
      <div>
        <p className="text-sm text-fg-body mb-2">{t('Content Generation')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {aiGenerationOptions.map(renderOptionCard)}
        </div>
      </div>
    </div>
  );
};

interface MatterFormProps {
  onClose: () => void;
  clientId?: string;
  /**
   * Hands the run to the list page: the PubSub group to keep listening on,
   * and which matter the correlation id belongs to. Without the second half
   * the list cannot resolve a completion frame for a matter it has never
   * fetched - which is every matter created in this session.
   */
  onGroupJoined?: (
    groupName: string,
    info?: { matterUuid?: string; correlationId?: string },
  ) => void;
}

interface Person {
  id: string;
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
}

interface MatterFormValues {
  matterTitle: string;
  category: string;
  language: string;
  jurisdiction: string;
  clients: any[];
  collaborators: Collaborator[];
}

const MatterForm: React.FC<MatterFormProps> = ({ onClose, clientId, onGroupJoined }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const { hasSectionAccess } = useLicenseGate();
  const location = useLocation();

  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const people = useSelector((state: RootState) => state.orgPeople?.people as Person[] || []);
  const { isPreparingDoc, isConnecting, progress, isRunning } = useSelector(
    (state: RootState) => state.matters
  );
  /*
    The run in the shared AI model - the same PubSub events the legacy
    `progress` list is built from, folded through `normalizeAIEvent`. This is
    what the panel renders, and the reason a `narration` line shows the day
    the engine sends one.
  */
  const run = useSelector((state: RootState) => state.matters.run);

  // Intent: read from URL query param (?intent=quick|analyze), falls back to null (selector screen)
  const queryIntent = new URLSearchParams(location.search).get('intent') as MatterIntent | null;
  const [intent, setIntent] = useState<MatterIntent | null>(
    queryIntent === 'quick' || queryIntent === 'analyze' ? queryIntent : null
  );

  // Sync intent changes to URL
  const updateIntent = useCallback((newIntent: MatterIntent | null) => {
    setIntent(newIntent);
    const params = new URLSearchParams(location.search);
    if (newIntent) {
      params.set('intent', newIntent);
    } else {
      params.delete('intent');
    }
    history.replace({ pathname: location.pathname, search: params.toString() });
  }, [history, location.pathname, location.search]);
  const [clients, setClients] = useState<any[]>([]);
  const [clientRoleOptions, setClientRoleOptions] = useState<DropdownOption[]>([]);
  const [languages, setLanguages] = useState<DropdownOption[]>([]);
  const [jurisdictions, setJurisdictions] = useState<DropdownOption[]>([]);
  const [categories, setCategories] = useState<DropdownOption[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [projectOptions, setProjectOptions] = useState<DropdownOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoAddClient, setAutoAddClient] = useState<{ ClientID: string; ClientRoles: string[] } | null>(null);
  /**
   * Defaults come from the store, synchronously - the platform bootstrap has
   * already hydrated them, so both selectors preselect on the first render.
   * They used to be fetched here, piggybacked on `/matters/dropdowns`, which
   * meant the form mounted with empty fields and filled them in a beat later.
   */
  const aiPreferences = useAIPreferences();
  const defaultLanguage = aiPreferences.language;
  const defaultJurisdiction = aiPreferences.jurisdiction;
  // Org default currency (#2246), cached via useOrgDefaultCurrency; 'usd' fallback.
  const { defaultCurrency: orgDefaultCurrency } = useOrgDefaultCurrency();
  const defaultCurrency = orgDefaultCurrency || 'usd';
  const [dataSources, setDataSources] = useState<DropdownOption[]>([]);
  // Title persists across intent switches
  const [persistedTitle, setPersistedTitle] = useState<string>('');

  // ── Intent selection tour (first time user sees Quick Create vs Analyze) ──
  const {
    isRunning: isIntentTourRunning,
    handleJoyrideCallback: handleIntentTourCallback,
  } = useFeatureTour({
    tourId: 'matter-intent',
    steps: matterIntentTourSteps,
    readyToShow: !intent && !isLoadingData,
    autoStartDelay: 500,
  });

  const matterIdRef = useRef<string | undefined>(undefined);
  const correlationIdRef = useRef<string | null>(null);
  const matterGroupRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    const loadData = async () => {
      setIsLoadingData(true);
      try {
        const dropdownsData = await dispatch(fetchMatterDropdowns());
        if (dropdownsData) {
          setLanguages(dropdownsData.languages || []);
          // Jurisdictions come from storage-account discovery (backend); fall
          // back to the static list only when the backend list is empty.
          setJurisdictions(
            dropdownsData.jurisdictions && dropdownsData.jurisdictions.length > 0
              ? dropdownsData.jurisdictions
              : AVAILABLE_JURISDICTIONS,
          );
          setClientRoleOptions(dropdownsData.clientRoles || []);

          // Deliberately NOT reading `dropdownsData.aiSettings` - this call is
          // for the OPTION LISTS only. The selected defaults come from the
          // store (see `aiPreferences` above), so they do not wait on it.
        }

        const categoriesResponse = await apis({
          method: 'GET',
          url: '/dropdown/matter-categories',
          params: { active_only: true }
        });

        if (categoriesResponse?.data?.data) {
          const formattedCategories = categoriesResponse.data.data.map((cat: any) => ({
            value: String(cat.id || cat.category_id || ''),
            label: cat.name || cat.category_name || ''
          }));
          setCategories(formattedCategories);
        }

        const clientsResponse = await apis({
          method: 'GET',
          url: '/clients',
          params: { page: '1', page_size: '100', is_active: 'true' }
        });

        if (clientsResponse?.data?.data) {
          setClients(clientsResponse.data.data);
        }

        // Fetch available data sources for the organization only when the org
        // has licensed the Knowledge Base (`data_sources`) section - attaching
        // a data source to a matter is a cross-feature "connect" affordance, so
        // it is gated on the section, not on the indexing AI feature. When
        // unlicensed the picker simply does not appear (premium add-on picker).
        if (hasSectionAccess('data_sources')) {
          try {
            const dsResult: any = await dispatch(fetchDataSources({ page: 1, limit: 100 }));
            if (dsResult?.dataSources) {
              const readySources = dsResult.dataSources
                .filter((ds: any) => {
                  const idxStatus = (ds.indexer_status || '').toLowerCase();
                  const dsStatus = (ds.status || '').toLowerCase();
                // Accept datasources that have been fully indexed and are active/ready
                return (
                  ['success', 'completed'].includes(idxStatus) ||
                  ['ready', 'active'].includes(dsStatus)
                );
              })
              .map((ds: any) => ({
                value: ds.uuid,
                label: ds.name || t('Unnamed Data Source')
              }));
            setDataSources(readySources);
          }
        } catch (dsError) {
          console.warn('[MatterForm] Failed to fetch data sources:', dsError);
        }
        }

        await dispatch(fetchOrgPeople());

        // Fetch linkable projects
        try {
          const projects = await dispatch(fetchLinkableProjects());
          if (projects && Array.isArray(projects)) {
            setProjectOptions(projects.map((p: any) => ({ value: p.uuid, label: p.title })));
          }
        } catch (projErr) {
          // Non-critical
        }
      } catch (error: any) {
        console.error('[MatterForm] Error loading data:', error?.message);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, [dispatch]);

  const buildClientOptions = (): DropdownOption[] => {
    return clients
      .filter(client => client.is_active !== false)
      .map(client => ({
        value: String(client.uuid || client.ClientID || client.id || ''),
        label: String(client.Name || client.name || t('Unknown Client'))
      }));
  };

  const handleAddClientClick = () => {
    const onClientCreated = (newClient: any) => {
      const clientId = String(newClient.uuid || newClient.id || '');
      const clientName = newClient.name || t('Unknown Client');

      (async () => {
        try {
          const clientsResponse = await apis({
            method: 'GET',
            url: '/clients',
            params: { page: '1', page_size: '15' }
          });

          if (clientsResponse?.data?.data) {
            setClients(clientsResponse.data.data);
          }

          // Auto-select the newly created client in the ClientSelector
          if (clientId) {
            // Small delay to ensure clients state + clientOptions have re-rendered
            setTimeout(() => {
              setAutoAddClient({ ClientID: clientId, ClientRoles: [] });
            }, 100);
          }

          dispatch(
            showFeedbackModal({
              showModal: true,
              message: `Client "${clientName}" created successfully.`,
              modalType: 'success',
              duration: 3000
            })
          );
        } catch (err: any) {
          console.error('Failed to refetch clients:', err?.message);
        }
      })();
    };

    dispatch(showAddClientSidePanel(onClientCreated));
  };

  const handleAiProcessingMessage = useCallback(
    (raw: any) => {
      const event = Array.isArray(raw) ? raw[0] : raw || {};
      const innerData = event.data || {};
      const data = { ...event, ...innerData };

      const eventCorrelationId = data.correlationId || data.correlation_id || data.CorrelationID || null;
      const eventMatterUuid = data.matter_uuid || data.matterUuid || data.MatterUUID || null;

      if (eventCorrelationId && !correlationIdRef.current) {
        correlationIdRef.current = eventCorrelationId;
      }

      if (eventMatterUuid && !matterIdRef.current) {
        matterIdRef.current = eventMatterUuid;
      }

      /*
        The whole envelope, folded into the shared AI run - this is what the
        panel renders. It goes first and unconditionally, so nothing the
        engine sends can be lost to the legacy branches below: the optional
        `narration` line, `facts`, per-document rows and the terminal states
        all ride in on keys those branches never read. `normalizeAIEvent`
        drops the frames that say nothing (hub chatter, the generic welcome).
      */
      dispatch({ type: 'MATTER_CREATION_AI_EVENT', payload: raw });

      const rawStatus = data.status ?? '';
      const status = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : String(rawStatus).toLowerCase();
      const subType = data.sub_type || data.event_name || data.eventType || data.event_type || null;
      const stepName = data.step_name || data.stepName || data.step || null;

      const isSystemInit = subType === 'matter_initiated' || subType === 'matter.initiated' || (status === 'started' && Array.isArray(data.steps));
      const isCompletion = status === 'completed' || subType === 'complete' || data.event_name === 'ai_processing_complete';
      const isFailure = status === 'failed' || data.error;
      const isWelcome = stepName === 'welcome' || subType === 'welcome' || data.message?.toLowerCase().includes('preparing');
      const isTechnicalMessage = (stepName === 'system' || subType === 'system' || subType === 'connected' || subType === 'disconnected') && !data.message;

      if (isTechnicalMessage) {
        return;
      }

      if (isWelcome) {
        dispatch({ type: 'MATTER_CREATION_CONNECTED' });
        return;
      }

      if (isSystemInit) {
        const normalizedSteps = (data.steps || []).map((s: any) => ({
          step: s.step || s.step_name || 'Matter Creation',
          status: s.status || 'pending',
          messages: s.messages || [],
        }));

        const idToUse = eventCorrelationId || correlationIdRef.current;
        if (idToUse && data.startTime) {
          localStorage.setItem(`matter_creation_start:${idToUse}`, data.startTime);
        }

        dispatch({
          type: 'MATTER_CREATION_PROGRESS_INIT',
          payload: { steps: normalizedSteps }
        });
        dispatch({ type: 'MATTER_CREATION_CONNECTED' });
        return;
      }

      const message = data.message || '';
      const elapsedSeconds = data.elapsed_seconds;
      const iconIdentifier = data.icon_identifier;
      const mappedPayload = {
        step: stepName || 'Matter Creation',
        message,
        status: data.status,
        progress_percent: data.progress_percent,
        elapsedSeconds,
        iconIdentifier,
        ...data,
      };
      const hasRealStep = Boolean(stepName);

      if (subType === 'step_started') {
        if (hasRealStep) {
          dispatch({
            type: 'MATTER_CREATION_STEP_STARTED',
            payload: {
              step: mappedPayload.step,
              elapsedSeconds,
              iconIdentifier,
              progress_percent: data.progress_percent,
            },
          });

          if (mappedPayload.message) {
            dispatch({
              type: 'MATTER_CREATION_STEP_MESSAGE',
              payload: {
                ...mappedPayload,
                elapsedSeconds,
                iconIdentifier,
                progress_percent: data.progress_percent,
              },
            });
          }
        }
        return;
      }

      if (subType === 'step_progress') {
        if (mappedPayload.message) {
          dispatch({
            type: 'MATTER_CREATION_STEP_MESSAGE',
            payload: {
              ...mappedPayload,
              elapsedSeconds,
              iconIdentifier,
              progress_percent: data.progress_percent,
            },
          });
        }
        return;
      }

      if (subType === 'step_completed') {
        if (hasRealStep) {
          dispatch({
            type: 'MATTER_CREATION_STEP_COMPLETED',
            payload: {
              step: mappedPayload.step,
              message: mappedPayload.message || 'Step completed.',
              elapsedSeconds,
              iconIdentifier,
              progress_percent: data.progress_percent,
              ...mappedPayload,
            },
          });
        }
        return;
      }

      if (isCompletion) {
        if (hasRealStep) {
          dispatch({
            type: 'MATTER_CREATION_STEP_COMPLETED',
            payload: {
              step: mappedPayload.step,
              message: mappedPayload.message || 'Matter creation completed.',
              progress_percent: 100,
              ...mappedPayload,
            },
          });
        }

        dispatch({
          type: 'MATTER_CREATION_COMPLETE',
          payload: { ...mappedPayload, progress_percent: 100 },
        });

        /*
          Landing on the finished matter is NOT done here.

          This callback runs outside React, from a socket frame, in the same
          tick as the completion dispatch that tears the progress surface
          down - and the `history.push` that used to sit at the end of it
          raced that teardown, which is where the `BrowserRouter` error came
          from. Worse, it was unreachable anyway: the `mountedRef` guard it
          hid behind is cleared by StrictMode's simulated remount and was
          never set back, so in development the push never ran at all and the
          run ended on a blank wizard.

          The uuid this needs was already taken at the top of the handler,
          before any dispatch - the event's own id is the fallback for a run
          that reconnected without one - and `useEffect` below does the
          navigating, after the completed frame has rendered.
        */

        // if (suggestedTasks && suggestedTasks.length > 0) {
        //   const taskCreationMatterUuid = completedMatterUuid;

        //   dispatch(showModal({
        //     type: 'suggestedTasks',
        //     tasks: suggestedTasks,
        //     message: 'Create suggested tasks for this matter?',
        //     subMsg: 'We\'ve identified some action items based on the matter details. You can create them now or skip.',
        //     matterUuid: taskCreationMatterUuid,
        //     onConfirm: async (selectedTasks: any[]) => {
        //       if (!selectedTasks || selectedTasks.length === 0) {
        //         return;
        //       }

        //       try {
        //         const tasksForCreation = selectedTasks.map(task => ({
        //           title: task.TaskTitle,
        //           description: task.TaskDescription,
        //           due_date: task.TaskDeadline,
        //           status: 'todo',
        //           priority: 'medium'
        //         }));

        //         if (!taskCreationMatterUuid) {
        //           throw new Error('Matter UUID is required for task creation');
        //         }

        //         const result = await dispatch(createBulkTasks(tasksForCreation, taskCreationMatterUuid));

        //         if (result && result.created_count > 0) {
        //           dispatch(showFeedbackModal({
        //             showModal: true,
        //             message: `Created ${result.created_count} of ${selectedTasks.length} tasks successfully.${result.failed_count > 0 ? ` ${result.failed_count} failed.` : ''}`,
        //             modalType: 'success',
        //             duration: 3000
        //           }));
        //         } else {
        //           dispatch(showFeedbackModal({
        //             modalType: 'error',
        //             message: 'No tasks were created. Please try again.',
        //             showModal: true,
        //             duration: 3000
        //           }));
        //         }
        //       } catch (error) {
        //         dispatch(showFeedbackModal({
        //           modalType: 'error',
        //           message: 'Failed to create some or all tasks. Please try again.',
        //           showModal: true,
        //           duration: 3000
        //         }));
        //       }

        //       dispatch(hideModal());
        //       history.push(`/matters/${completedMatterUuid}`);
        //     },
        //     onCancel: () => {
        //       dispatch(hideModal());
        //       history.push(`/matters/${completedMatterUuid}`);
        //     },
        //     showModal: false
        //   }));
        // }
        return;
      }

      if (isFailure) {
        const errorMessage = toErrorText(data.error) || toErrorText(mappedPayload.message) || 'AI Processing failed';

        if (hasRealStep) {
          dispatch({
            type: 'MATTER_CREATION_STEP_FAILED',
            payload: {
              step: mappedPayload.step || 'Matter Creation',
              message: errorMessage,
              ...mappedPayload,
            },
          });
        }

        dispatch({
          type: 'MATTER_CREATION_FAILED',
          payload: mappedPayload,
        });
        return;
      }

      if (mappedPayload.message) {
        if (hasRealStep) {
          dispatch({
            type: 'MATTER_CREATION_STEP_MESSAGE',
            payload: mappedPayload,
          });
        } else {
          if (mappedPayload.progress_percent !== undefined) {
            dispatch({
              type: 'MATTER_CREATION_STEP_MESSAGE',
              payload: {
                ...mappedPayload,
                step: 'Matter Processing',
              },
            });
          }
        }
      }
    },
    [dispatch]
  );

  /**
   * The documents a submission actually carries.
   *
   * A typed description is uploaded as a one-file text document, exactly as
   * before - it is the matter's only source when no files were picked.
   */
  const documentsFrom = (filesOrDescription: { files?: File[]; description?: string }): File[] => {
    const files = filesOrDescription.files || [];
    if (files.length > 0) return files;
    const description = (filesOrDescription.description || '').trim();
    if (!description) return [];
    const blob = new Blob([description], { type: 'text/plain' });
    return [new File([blob], 'matter_description.txt', { type: 'text/plain' })];
  };

  /**
   * Send the matter's documents up AFTER it exists, in bounded batches.
   *
   * The whole point of the split: a reset now costs the files in one batch,
   * not the matter. So when something is missing the user is told which files
   * and left on a real matter they can retry from - never silently handed a
   * thin analysis, and never a raw ERR_HTTP2_PROTOCOL_ERROR.
   *
   * Returns true when everything landed.
   */
  const uploadMatterDocuments = async (matterUuid: string, documents: File[]): Promise<boolean> => {
    if (!documents.length) return true;

    const { accepted, oversized } = partitionOversized(documents);
    const result = accepted.length
      ? ((await dispatch(addFilesToMatter(matterUuid, accepted) as any)) as unknown as SequentialUploadResult)
      : null;

    const notes = [
      oversized.length ? oversizedMessage(oversized) : null,
      result ? describeUploadResult(result) : null,
    ].filter(Boolean) as string[];

    if (!notes.length) return true;

    const landed = result?.uploadedCount ?? 0;
    dispatch(
      showFeedbackModal({
        showModal: true,
        modalType: 'warning',
        message: t('{{landed}} of {{total}} documents were added to the matter.', {
          landed,
          total: documents.length,
        }),
        subMsg: notes.join(' '),
        duration: 10000,
      })
    );
    return false;
  };

  // ── Quick Create submit (BaseForm) ──
  const handleQuickCreateSubmit = async (formData: Record<string, any>) => {
    setIsSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('MatterName', formData.matterTitle || '');
      if (formData.category) payload.append('CategoryID', String(formData.category));
      // Language follows the same rule as jurisdiction: the chosen value, else
      // the user's preference, else nothing at all. There is no platform
      // language default to invent - unset means "answer neutrally".
      const quickLanguage = String(formData.language || defaultLanguage || '').trim();
      if (quickLanguage) payload.append('Language', quickLanguage);
      // No client-side jurisdiction fallback: send the chosen value (or the
      // user's preference) explicitly, otherwise omit the key entirely and let
      // the backend resolver own the decision.
      const quickJurisdiction = String(formData.jurisdiction || defaultJurisdiction || '').trim();
      if (quickJurisdiction) payload.append('Jurisdiction', quickJurisdiction);

      if (formData.clients && Array.isArray(formData.clients)) {
        const validClients = formData.clients.filter(
          (c: any) => c.ClientID && String(c.ClientID).trim() !== ''
        );
        if (validClients.length > 0) {
          payload.append('Clients', JSON.stringify(validClients));
        }
      }

      if (formData.collaborators && formData.collaborators.length > 0) {
        /*
          POST /matters/create reads each entry as `user_id` (numeric) OR
          `user_uuid` (plaintext uuid), plus `access` and `role`. Collapsing
          both ids into `user_uuid` meant a row that only had a numeric id was
          sent as a uuid, and the backend's uuid lookup could never match it —
          the collaborator was silently dropped. Send each id under its own
          key and let the numeric fast path win when it exists.
        */
        const mappedCollaborators = formData.collaborators.map((collab: any) => ({
          ...(collab.user_id != null && collab.user_id !== ''
            ? { user_id: collab.user_id }
            : {}),
          ...(collab.collaborator_uuid ? { user_uuid: collab.collaborator_uuid } : {}),
          access: collab.access || 'edit',
          role: collab.role || 'collaborator',
        }));
        payload.append('Collaborators', JSON.stringify(mappedCollaborators));
      }

      if (formData.projectUUID) {
        payload.append('project_uuid', formData.projectUUID);
      }

      payload.append('skip_processing', 'true');

      // Metadata only. The documents follow in bounded batches once the matter
      // exists - one multipart body carrying all of them is what the EU edge
      // resets, and it used to take the whole matter down with it.
      const documents = documentsFrom(formData.filesOrDescription || {});

      const newMatter = await dispatch(createMatter(payload));
      const matterUuid = newMatter?.matter?.uuid || newMatter?.uuid;

      if (!matterUuid) {
        throw new Error('Matter created but no UUID returned');
      }

      const complete = await uploadMatterDocuments(matterUuid, documents);

      setIsSubmitting(false);
      if (complete) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Matter created successfully'),
            modalType: 'success',
            duration: 3000
          })
        );
      }
      // `replace`: the URL behind this one is the wizard that has just been
      // submitted, and Back belongs to the matter list.
      history.replace(matterDetailRoute(matterUuid));
    } catch (error: any) {
      console.error('[MatterForm] Quick create error:', error);
      setIsSubmitting(false);
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: t('Failed to create matter. Please try again.'),
          modalType: 'error',
          duration: 5000
        })
      );
    }
  };

  // ── Wizard submit (Create & Analyze) ──
  const handleWizardSubmit = async (data: MatterWizardData) => {
    setIsSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('MatterName', data.matterTitle);
      if (data.category) payload.append('CategoryID', String(data.category));
      const wizardLanguage = String(data.language || defaultLanguage || '').trim();
      if (wizardLanguage) payload.append('Language', wizardLanguage);
      // Same rule as Quick Create - no hardcode, omit rather than invent.
      const wizardJurisdiction = String(data.jurisdiction || defaultJurisdiction || '').trim();
      if (wizardJurisdiction) payload.append('Jurisdiction', wizardJurisdiction);

      if (data.clients.length > 0) {
        const validClients = data.clients.filter(
          (c) => c.ClientID && String(c.ClientID).trim() !== ''
        );
        if (validClients.length > 0) {
          payload.append('Clients', JSON.stringify(validClients));
        }
      }

      if (data.collaborators.length > 0) {
        /*
          POST /matters/create reads each entry as `user_id` (numeric) OR
          `user_uuid` (plaintext uuid), plus `access` and `role`. Collapsing
          both ids into `user_uuid` meant a row that only had a numeric id was
          sent as a uuid, and the backend's uuid lookup could never match it —
          the collaborator was silently dropped. Send each id under its own
          key and let the numeric fast path win when it exists.
        */
        const mappedCollaborators = data.collaborators.map((collab: any) => ({
          ...(collab.user_id != null && collab.user_id !== ''
            ? { user_id: collab.user_id }
            : {}),
          ...(collab.collaborator_uuid ? { user_uuid: collab.collaborator_uuid } : {}),
          access: collab.access || 'edit',
          role: collab.role || 'collaborator',
        }));
        payload.append('Collaborators', JSON.stringify(mappedCollaborators));
      }

      if (data.projectUUID) {
        payload.append('project_uuid', data.projectUUID);
      }

      // Metadata only - the documents go up separately, after the matter
      // exists, and analysis is triggered as its own call once they land.
      const documents = documentsFrom(data.filesOrDescription);
      payload.append('skip_processing', 'true');

      if (data.dataSources.length > 0) {
        payload.append('DataSourceUUIDs', JSON.stringify(data.dataSources));
      }

      if (data.selectedIndexes.length > 0) {
        payload.append('SelectedIndexes', JSON.stringify(data.selectedIndexes));
      }

      const opts = data.analysisOptions;
      payload.append('FetchApplicableLaws', String(opts.fetchLaws ?? true));
      payload.append('FetchPrecedents', String(opts.fetchPrecedents ?? true));
      payload.append('FetchDataFromOwnDataSource', String(opts.fetchDataSource ?? true));
      payload.append('CreateTasks', String(opts.createTasks ?? true));
      payload.append('CreateQuestionnaire', String(opts.createQuestionnaire ?? true));

      // Matter Economics - pricing fields. Wire format per MATTER_ECONOMICS_API.md:
      // PascalCase keys, lowercase currency, decimal major units, contingency as a
      // fraction in [0,1] (the UI captures whole-number percent).
      if (isMatterEconomicsEnabled() && data.pricing) {
        const p = data.pricing;
        payload.append('PricingModel', p.pricing_model);
        if (p.currency) payload.append('Currency', p.currency.toLowerCase());
        if (requiresFixedFee(p.pricing_model) && p.fixed_fee_amount.trim() !== '') {
          payload.append('FixedFeeAmount', String(Number(p.fixed_fee_amount)));
        }
        // Budget applies to every pricing model, so it is not gated on one.
        // Omitted rather than sent empty when the field is left blank - the
        // create path has nothing to clear.
        if (p.budget_amount?.trim()) {
          payload.append('BudgetAmount', String(Number(p.budget_amount)));
        }
        if (requiresContingency(p.pricing_model) && p.contingency_pct.trim() !== '') {
          payload.append('ContingencyPct', String(Number(p.contingency_pct) / 100));
        }
      }

      const newMatter = await dispatch(createMatter(payload));
      const matterUuid = newMatter?.matter?.uuid || newMatter?.uuid;

      if (!matterUuid) {
        throw new Error('Matter creation did not return a UUID');
      }

      matterIdRef.current = matterUuid;

      await uploadMatterDocuments(matterUuid, documents);

      // Analysis is now its own call, made only once the documents are in
      // place - otherwise the engine would run against an empty matter.
      const analysis = await dispatch(
        analyzeMatter(matterUuid, { ...data.analysisOptions }, data.dataSources, data.selectedIndexes) as any
      ) as any;

      // Correlation casing differs per endpoint and is NOT normalized on the
      // backend: /analyze answers with a top-level `correlationId`, /create
      // nests it under `processing`. Take whichever this response carries.
      const correlationId =
        analysis?.correlationId ||
        analysis?.correlation_id ||
        analysis?.processing?.correlationId ||
        newMatter?.processing?.correlationId ||
        newMatter?.processing?.correlation_id ||
        newMatter?.correlation_id ||
        newMatter?.correlationId;

      // Hand the run to the matter's OWN url: navigate straight to /matters/<uuid>
      // so the create form is freed (the user can start another matter right
      // away - nothing blocks on this run) and the matter detail page owns the
      // live processing view. The detail page rejoins the `ai_processing` hub
      // from the correlation id, passed here as nav state for an instant connect,
      // and also persisted on the matter as processing_correlation_id so a cold
      // click-in later still resumes the processing view.
      history.replace({
        pathname: matterDetailRoute(matterUuid),
        state: { processingCorrelationId: correlationId || null },
      });

      setIsSubmitting(false);
    } catch (error: any) {
      console.error('[MatterForm] Analyze submission error:', error);
      setIsSubmitting(false);
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: t('Failed to create matter. Please try again.'),
          modalType: 'error',
          duration: 5000
        })
      );
    }
  };

  const handleCancelMatterCreation = useCallback(async () => {
    try {
      const multiHubService = getMultiHubSocketService();
      if (matterGroupRef.current) {
        multiHubService.emit('ai_processing', 'leave', { group: matterGroupRef.current });
      }
      multiHubService.off('ai_processing', '*', handleAiProcessingMessage);

      if (matterIdRef.current) {
        try {
          await dispatch(deleteMatter(matterIdRef.current));
        } catch (error) {
          console.error('[MatterForm] Failed to delete cancelled matter:', error);
        }
      }

      dispatch({ type: 'MATTER_CREATION_CANCELLED' });
      setIsSubmitting(false);
      onClose();
    } catch (error) {
      console.error('[MatterForm] Cancel error:', error);
      dispatch({ type: 'MATTER_CREATION_CANCELLED' });
      setIsSubmitting(false);
      onClose();
    }
  }, [dispatch, onClose, handleAiProcessingMessage]);

  /**
   * The end of a successful run: open the matter it just created.
   *
   * This is an effect and not a line at the end of the socket handler on
   * purpose. The handler runs outside React, from a PubSub frame, in the same
   * tick as the dispatch that flips the run to `completed` and re-renders
   * this form - a `history.push` from there raced its own teardown and was
   * reported against `BrowserRouter`. An effect runs after that render has
   * committed, so the navigation is an ordinary one from a mounted component.
   *
   * `replace`, not `push`: the URL being left behind is `?add=true&intent=…`,
   * a wizard that has already been submitted. Back from the new matter should
   * reach the matter list, not a spent form.
   *
   * No uuid - a reconnected run whose events never named the matter - is the
   * one case that cannot navigate. It holds the completed panel instead of
   * dropping the user on a blank wizard, and `onClose` is the way out.
   */
  const navigatedRef = useRef(false);
  useEffect(() => {
    const destination = createdMatterDestination(
      run.status,
      matterIdRef.current,
      navigatedRef.current,
    );
    if (!destination) return;
    navigatedRef.current = true;
    history.replace(destination);
  }, [run.status, history]);

  const openCreatedMatter = useCallback(() => {
    const destination = createdMatterRoute(matterIdRef.current);
    if (!destination) return;
    navigatedRef.current = true;
    history.replace(destination);
  }, [history]);

  // ── Always reset matter-creation Redux state + PubSub on unmount ──
  useEffect(() => {
    /*
      StrictMode mounts, tears down and re-mounts every component in
      development. This flag has to be re-armed by the second mount: left
      alone, it stayed false for the rest of the form's life and every guard
      standing on it - including the one that used to wrap the completion
      navigation - was dead from the first paint.
    */
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      const multiHubService = getMultiHubSocketService();
      if (matterGroupRef.current) {
        multiHubService.emit('ai_processing', 'leave', { group: matterGroupRef.current });
      }
      multiHubService.off('ai_processing', '*', handleAiProcessingMessage);

      dispatch({ type: 'MATTER_CREATION_CANCELLED' });
    };
  }, [dispatch, handleAiProcessingMessage]);

  // ── Quick Create: BaseForm sections & schema ──
  const quickCreateSections: BaseFormSectionConfig[] = useMemo(() => {
    return [
      {
        fields: [
          {
            name: 'matterTitle',
            type: 'input',
            label: t('Matter Title'),
            placeholder: t('e.g. Smith v. Johnson, IP License Review, Tenant Dispute...'),
            required: true,
            gridSize: 'full'
          } as BaseFormFieldConfig
        ]
      },
      ...(jurisdictions.length > 0 ? [{
        fields: [
          {
            name: 'jurisdiction',
            type: 'dropdown' as const,
            label: t('Jurisdiction'),
            // Defaults to the user's AI preference; always overridable here.
            placeholder: t('Select Jurisdiction'),
            required: false,
            customProps: { fullWidth: true },
            gridSize: 'full' as const,
            options: jurisdictions,
          } as BaseFormFieldConfig,
        ],
      }] : []),
      ...(projectOptions.length > 0 ? [{
        fields: [
          {
            name: 'projectUUID',
            type: 'dropdown' as const,
            label: t('Link to Project'),
            placeholder: t('Select a project (optional)'),
            required: false,
            customProps: { fullWidth: true },
            gridSize: 'full' as const,
            options: projectOptions,
          } as BaseFormFieldConfig,
        ],
      }] : []),
      {
        title: t('Attach Files'),
        description: t('Optionally upload documents now — you can always add more later.'),
        fields: [
          {
            name: 'filesOrDescription',
            type: 'custom',
            label: t('Files'),
            customComponent: FileOrDescriptionSelector,
            required: false,
            gridSize: 'full'
          } as BaseFormFieldConfig
        ]
      },
    ];
  }, [t, projectOptions, jurisdictions]);

  /**
   * Quick Create seed values. The jurisdiction starts on the user's AI
   * preference (which arrives async with the dropdowns) - BaseForm adopts a
   * changed initial value but never overwrites a field the user has touched,
   * so the default stays fully overridable.
   */
  const quickCreateInitialValues = useMemo(() => ({
    matterTitle: persistedTitle,
    jurisdiction: defaultJurisdiction,
    filesOrDescription: { files: [], description: '' },
  }), [persistedTitle, defaultJurisdiction]);

  const quickCreateSchema = useMemo(() => {
    const s = new FormSchema('QuickCreateMatterSchema');
    s.addField(new FormField('matterTitle', 'text', true, t('Matter Title'), {
      customValidator: (value: any) => {
        if (!value || !String(value).trim()) {
          return { isValid: false, error: t('Matter title is required') };
        }
        return { isValid: true, sanitized: String(value).trim() };
      }
    }));
    s.addField(new FormField('jurisdiction', 'text', false, t('Jurisdiction')));
    s.addField(new FormField('filesOrDescription', 'text', false, t('Files')));
    return s;
  }, [t]);

  // Persist title when switching intents
  const handleQuickCreateValuesChange = useCallback((values: Record<string, any>, changedField: string) => {
    if (changedField === 'matterTitle') {
      setPersistedTitle(values.matterTitle || '');
    }
  }, []);

  // ── Render ──
  if (isLoadingData || aiPreferences.isHydrating) {
    return <Skeleton variant="form" rows={6} />;
  }

  /*
    Only the submit itself gets the bare loader. A run that is live but has not
    reported a step yet used to land here too - a lone logo on an empty screen
    for however long the engine took to say its first word. The tracker below
    covers that beat with its connecting stage instead.
  */
  if (isSubmitting) {
    return (
      <div className="flex items-center justify-center h-screen bg-bg">
        <BrandLoader size="lg" />
      </div>
    );
  }

  /*
    The run's own surface.

    A FINISHED run holds it too, which it did not before: `completed` clears
    `isRunning`, `isConnecting` and `progress`, so the moment the engine said
    it was done this fell through to the wizard below and the user watched a
    successful run end on a blank Details step. Completion navigates away
    from an effect above; the panel is what is on screen until it does, and
    what stays if there is no matter to open. A failure keeps it as well —
    the engine's reason for it is shown nowhere else.
  */
  if (
    isPreparingDoc ||
    isConnecting ||
    isRunning ||
    progress.length > 0 ||
    isRunLive(run) ||
    isRunFinished(run)
  ) {
    return (
      <div className="h-screen flex justify-center items-center bg-bg">
        <MatterCreationProgress
          run={run}
          correlationId={correlationIdRef.current ?? undefined}
          onCancel={handleCancelMatterCreation}
          onClose={onClose}
          onOpenMatter={matterIdRef.current ? openCreatedMatter : undefined}
        />
      </div>
    );
  }

  // ── Intent selection screen ──
  if (!intent) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto custom-scrollbar-main">
          <div className="max-w-2xl mx-auto px-6 py-10">
            <div className="text-center mb-8">
              <h1 className="text-lg text-fg-strong">
                {t('Create a New Matter')}
              </h1>
              <p className="mt-2 text-sm text-fg-muted">
                {t('How would you like to get started?')}
              </p>
            </div>
            <WizardStepTip
              tipId="matter-intent-choice"
              title={matterIntentTip.title}
              content={matterIntentTip.content}
            />
            <MatterIntentSelector value={null} onChange={updateIntent} />
          </div>
        </div>
        <div className="glass-bar border-t border-hairline px-6 py-4">
          <div className="mx-auto w-full flex justify-end gap-3 max-w-2xl">
            <Button
              type="button"
              onClick={onClose}
              variant='neutral'
              size='small'
            >
              {t('Cancel')}
            </Button>
          </div>
        </div>
        <FeatureTour
          steps={matterIntentTourSteps}
          run={isIntentTourRunning}
          onCallback={handleIntentTourCallback}
          disableOverlay
        />
      </div>
    );
  }

  // ── Create & Analyze wizard ──
  if (intent === 'analyze') {
    return (
      <MatterWizard
        initialTitle={persistedTitle}
        categories={categories}
        languages={languages}
        jurisdictions={jurisdictions}
        clientOptions={buildClientOptions()}
        clientRoleOptions={clientRoleOptions}
        dataSourceOptions={dataSources}
        projectOptions={projectOptions}
        defaultLanguage={defaultLanguage}
        defaultJurisdiction={defaultJurisdiction}
        defaultCurrency={defaultCurrency}
        autoAddClient={autoAddClient}
        onAddNewClient={handleAddClientClick}
        onSubmit={handleWizardSubmit}
        onCancel={onClose}
        onBack={() => updateIntent(null)}
        isSubmitting={isSubmitting}
      />
    );
  }

  // ── Quick Create form ──
  return (
    <div className="flex flex-col h-full">
      <div className="max-w-3xl mx-auto w-full px-6 pt-4">
        <WizardStepTip
          tipId="matter-quick-create"
          title={matterQuickCreateTip.title}
          content={matterQuickCreateTip.content}
        />
      </div>
      <BaseForm
        title={t('Quick Create')}
        description={t('Create a matter instantly. You can add clients, collaborators, and run PONS analysis anytime from the matter page.')}
        sections={quickCreateSections}
        schema={quickCreateSchema}
        initialValues={quickCreateInitialValues}
        onSubmit={handleQuickCreateSubmit}
        onCancel={onClose}
        onBack={() => updateIntent(null)}
        submitLabel={t('Create Matter')}
        cancelLabel={t('Cancel')}
        backLabel={t('Back')}
        maxWidth="max-w-3xl"
        onValuesChange={handleQuickCreateValuesChange}
      />
    </div>
  );
};

export default MatterForm;
